import { useMemo, useState } from "react";
import { Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Typography } from "@mui/material";
import { InfoOutlined } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import TextAnswerSubtypeDetails from "./details/TextAnswerSubtypeDetails";
import EvaluationStarsSubtypeDetails from "./details/EvaluationStarsSubtypeDetails";
import EvaluationThumbsSubtypeDetails from "./details/EvaluationThumbsSubtypeDetails";
import PictureUploadSubtypeDetails from "./details/PictureUploadSubtypeDetails";
import ChecklistAnswerSubtypeDetails from "./details/ChecklistAnswerSubtypeDetails";
import { ActionModule } from "../../../../../types/actionModule/actionModule";
import { ActionModuleInstanceResponse } from "../../../../../types/actionModule/actionModuleInstance";
import { BaseModuleSubtypeResponse, Subtype } from "../../../../../types/actionModule/baseModuleSubtype";
import { ChecklistAnswerSubtypeResponse, ChecklistAnswerSubtypeDataModel } from "../../../../../types/actionModule/subtypes/checklistAnswerModels";
import { EvaluationStarsSubtypeDataModel } from "../../../../../types/actionModule/subtypes/evaluationStarModels";
import { EvaluationThumbsSubtypeDataModel } from "../../../../../types/actionModule/subtypes/evaluationThumbsModels";
import { PictureUploadSubtypeResponse, PictureUploadSubtypeDataModel } from "../../../../../types/actionModule/subtypes/pictureUploadModels";
import { TextAnswerSubtypeDataModel } from "../../../../../types/actionModule/subtypes/textAnswerModels";
import MultiPictureUploadSubtypeDetails from "./details/MultiPictureUploadSubtypeDetails";
import { MultiPictureUploadSubtypeDataModel, MultiPictureUploadSubtypeResponse } from "../../../../../types/actionModule/subtypes/multiPictureUploadModels";
import { SurveyAppointmentSubtypeDataModel } from "../../../../../types/actionModule/subtypes/surveyAppointmentModels";
import SurveyAppointmentSubtypeDetails from "./details/SurveyAppointmentSubtypeDetails";
import { GeneralButton } from "../../../../../common/ui/GeneralButton";

interface Props {
    actionModule: ActionModule;
    instance: ActionModuleInstanceResponse;
}

export default function InstanceSubtypeDetailsCell(props: Props) {
    const [dialogOpen, setDialogOpen] = useState(false);

    return <>
        {
            props.instance.subtypeData.length > 0 &&
            <IconButton onClick={() => setDialogOpen(true)}>
                <InfoOutlined />
            </IconButton>
        }

        <InstanceSubtypeDetailsDialog
            open={dialogOpen}
            onClose={() => setDialogOpen(false)}
            {...props} />
    </>
}

interface InstanceSubtypeDetailsDialogProps extends Props {
    open: boolean;
    onClose: () => void;
}

function InstanceSubtypeDetailsDialog(props: InstanceSubtypeDetailsDialogProps) {
    const { t } = useTranslation();

    return (
        <Dialog
            open={props.open}
            onClose={props.onClose}
            fullWidth
            maxWidth="lg">
            <DialogTitle>{t("incentivationPage.instanceDetailsTitle")}</DialogTitle>
            <DialogContent>
                {
                    props.actionModule.subtypes.map(s => <>
                        {s.name && <Typography variant="subtitle1">{s.name}</Typography>}
                        <SubtypeDetails
                            actionModule={props.actionModule} subtype={s}
                            instance={props.instance} />
                    </>)
                }
            </DialogContent>
            <DialogActions>
                <GeneralButton
                    onClick={props.onClose}
                >
                    {t("buttons.ok")}
                </GeneralButton>
            </DialogActions>
        </Dialog>
    );
}

interface SubtypeDetailsProps {
    actionModule: ActionModule;
    subtype: BaseModuleSubtypeResponse;
    instance: ActionModuleInstanceResponse;
}

function SubtypeDetails(props: SubtypeDetailsProps) {
    const subtypeData = useMemo(
        () => props.instance.subtypeData.find(d => d.moduleSubtypeId == props.subtype.id),
        [props.instance, props.subtype]
    );

    if (subtypeData === undefined) {
        return <></>;
    }

    switch (props.subtype.subtype) {
        default:
            return <></>;
        case Subtype.TextAnswerModuleSubtype:
            return <TextAnswerSubtypeDetails
                subtype={props.subtype as ChecklistAnswerSubtypeResponse}
                subtypeData={subtypeData as TextAnswerSubtypeDataModel} />
        case Subtype.EvaluationStarsModuleSubtype:
            return <EvaluationStarsSubtypeDetails
                subtype={props.subtype}
                subtypeData={subtypeData as EvaluationStarsSubtypeDataModel} />
        case Subtype.EvaluationThumbsModuleSubtype:
            return <EvaluationThumbsSubtypeDetails
                subtype={props.subtype}
                subtypeData={subtypeData as EvaluationThumbsSubtypeDataModel} />
        case Subtype.MultiPictureUploadModuleSubtype:
            return <MultiPictureUploadSubtypeDetails
                subtype={props.subtype as MultiPictureUploadSubtypeResponse}
                subtypeData={subtypeData as MultiPictureUploadSubtypeDataModel} />
        case Subtype.PictureUploadModuleSubtype:
            return <PictureUploadSubtypeDetails
                subtype={props.subtype as PictureUploadSubtypeResponse}
                subtypeData={subtypeData as PictureUploadSubtypeDataModel} />
        case Subtype.ChecklistAnswerModuleSubtype:
            return <ChecklistAnswerSubtypeDetails
                subtype={props.subtype as ChecklistAnswerSubtypeResponse}
                subtypeData={subtypeData as ChecklistAnswerSubtypeDataModel} />
        case Subtype.SurveyAppointmentModuleSubtype:
            return <SurveyAppointmentSubtypeDetails
                subtype={props.subtype as BaseModuleSubtypeResponse}
                subtypeData={subtypeData as SurveyAppointmentSubtypeDataModel} />
    }
}